import { useEffect, useState } from "react"
import { AppDispatch, RootState } from "../../../redux/store"
import { useAppSelector } from "../../../redux/hooks"

import { ResourceChecklist } from "../resourceChecklist"
import { getResources, requestResourcesWithRelated } from "../../../datastore"
import { subDays } from "date-fns"
import { useTask } from "../../../hooks/useTask"
import { Project, ProjectSite } from "../../../datastore/models"
import { uniq } from "lodash"
import { resourceEventCombined } from "../../../datastore/resourceEventCombined"
import { ResourceEvent } from "../../../datastore/types"
import { selectIncidentsWithActions } from "../../../redux/selectors/incidents"
import { selectProjectSites } from "../../../redux/selectors/projectSites"

export const useIncidentReportsResources = (
  siteId: number | null | undefined,
  companyId: number | null | undefined,
  incidentsList: ResourceChecklist,
  refresh?: any
) => {
  const [checkedIncidents, setCheckedIncidents] = useState<number[]>([])
  useEffect(() => {
    // checkedIncidents = incidents already ticked in toolbox
    // Need to include these separately in selector because they may be 'closed out' during
    // meeting and will then no longer appear under 'open' list
    setCheckedIncidents(incidentsList.checked.map((item) => item.id))
  }, [incidentsList])

  const taskResult = useTask(
    async (dispatch: AppDispatch, getState: () => RootState, services: any) => {
      if (companyId) {
        // Requesting records for all company sites:

        // Request projectSites / sites for all open projects
        const projectsResult = await requestResourcesWithRelated(
          "projects",
          { eqFilter: { status: "OPEN" } },
          ["projectSites.sites"]
        )(dispatch, getState, services)

        const projects = getResources<Project>(
          getState().resources,
          "projects",
          { eqFilter: { company: companyId, status: "OPEN" } }
        )
        const projectSites = getResources<ProjectSite>(
          getState().resources,
          "projectSites",
          { inFilter: { project: projects.map((project) => project.id) } }
        )
        const siteIds = uniq(
          projectSites.map((projectSite) => projectSite.site)
        )

        const result = await requestResourcesWithRelated(
          "incidents",
          {
            inFilter: { site: siteIds },
            periodFilter: {
              attr: "incidentDate",
              periodStart: subDays(new Date(), 7)
            }
          },
          ["incidentActions"]
        )(dispatch, getState, services)

        return {
          isSuccess:
            resourceEventCombined(projectsResult, [result]) !==
            ResourceEvent.RESOURCE_LOAD_ERROR
        }
        
      } else {
        // Requesting records for specified site:
        const result = await requestResourcesWithRelated(
          "incidents",
          {
            eqFilter: { site: siteId },
            periodFilter: {
              attr: "incidentDate",
              periodStart: subDays(new Date(), 7)
            }
          },
          ["incidentActions"]
        )(dispatch, getState, services)

        return {
          isSuccess: result !== ResourceEvent.RESOURCE_LOAD_ERROR
        }
      }
    },
    [siteId, companyId, refresh],
    !!siteId || !!companyId
  )

  const siteIds = useAppSelector((state) =>
    selectProjectSites(state, { companyId })
  ).map((projectSite) => projectSite.siteId)

  return {
    incidents: useAppSelector((state) =>
      selectIncidentsWithActions(
        state,
        siteId
          ? {
              siteId,
              open: true,
              list: checkedIncidents
            }
          : {
              siteIds,
              open: true,
              list: checkedIncidents
            }
      )
    ),
    isLoading: taskResult.isLoading,
    isSuccess: taskResult.isSuccess,
    isError: taskResult.isError
  }
}
